import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DashboardV2 } from './DashboardV2';
import { AddInvestorWizard } from './AddInvestorWizard';
import { InvestorSlideOver } from './slideover/InvestorSlideOver';
import { InvestorCommandPalette } from './InvestorCommandPalette';
import { ArchivedInvestorsDialog } from './ArchivedInvestorsDialog';
import { InvestorThemeScope } from './shared/InvestorThemeScope';
import { useInvestors } from '@/hooks/useInvestors';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { InvestorService } from '@/services/investorService';
import type { Investor, InvestorFilters } from '@/types/investor';

// Fields the roster can inline-edit; diffed so a scoped patch (not a whole record) is saved.
const INLINE_DIFF_KEYS: Array<keyof Investor> = [
  'status', 'approvedAsOfDate', 'originalApprovedAsOfDate',
  'correspondentStatus', 'correspondentApprovedAsOfDate', 'originalCorrespondentApprovedAsOfDate',
  'correspondentLockedAt', 'canDoWholesale', 'canGoCorrespondent', 'delegatedType',
  'wholesaleProducts', 'correspondentProducts', 'wholesaleLenderPaidComp', 'wholesaleAdditionalLendwiseFee',
  'wholesaleWebsite', 'correspondentWebsite', 'wholesaleNotes', 'correspondentNotes',
  'wholesaleAeName', 'wholesaleAePhone', 'wholesaleAeEmail',
  'correspondentAeName', 'correspondentAePhone', 'correspondentAeEmail',
  'aeName', 'aePhone', 'aeEmail',
  'wholesaleEpoRule', 'wholesaleEpdRule', 'correspondentEpoRule', 'correspondentEpdRule', 'repurchaseRule',
  'wholesaleCanIntegrateOptimalBlue', 'wholesaleHasIntegratedOptimalBlue',
  'correspondentCanIntegrateOptimalBlue', 'correspondentHasIntegratedOptimalBlue'
];

type ChannelView = 'wholesale' | 'correspondent';

const defaultFilters: InvestorFilters = {
  search: '',
  status: 'All',
  productType: '',
  delegatedType: 'All',
  correspondentStatus: ['Approved', 'Pending'],
  wholesaleStatus: ['Approved', 'Pending'],
  wholesaleObIntegrated: 'All',
  correspondentObIntegrated: 'All',
  approvedDateRange: { startDate: '', endDate: '' },
  correspondentApprovedDateRange: { startDate: '', endDate: '' },
  createdDateRange: { startDate: '', endDate: '' },
  netWorthMin: '',
  netWorthMax: ''
};

/**
 * Manage Investors v2. Renders DashboardV2 (the mock-
 * styled roster, themed via InvestorThemeScope), the v2 create wizard,
 * adds a right slide-over for detail + the Processing/Closing tabs and a ⌘K palette.
 */
export const InvestorsPageV2: React.FC = () => {
  const {
    investors, archivedInvestors, loading, archivedLoading, organizationId,
    addInvestor, updateInvestor, archiveInvestor, bulkArchiveInvestors,
    loadArchivedInvestors, restoreInvestor, refreshInvestor
  } = useInvestors();
  const { hasAnyPermission } = useRoleAccess();
  // Every authenticated app user can view investors; mutations remain permission-gated.
  const canManage = hasAnyPermission(['edit_investors']);

  const [createOpen, setCreateOpen] = useState(false);
  const [createChannel, setCreateChannel] = useState<ChannelView | undefined>(undefined);
  const [selected, setSelected] = useState<Investor | null>(null);
  const [selectedEditOnOpen, setSelectedEditOnOpen] = useState(false);
  const [slideOverDirty, setSlideOverDirty] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);

  const [filters, setFilters] = useState<InvestorFilters>(defaultFilters);
  const [sortField, setSortField] = useState<keyof Investor>('investor');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // ⌘K / Ctrl+K toggles the command palette.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Keep the open slide-over in sync if its investor changes in the list.
  useEffect(() => {
    if (selected) {
      const fresh = investors.find((i) => i.id === selected.id);
      if (fresh && fresh !== selected) {
        setSelected(fresh);
      }
    }
  }, [investors, selected]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
          <p className="text-muted-foreground">Loading investor data...</p>
        </div>
      </div>
    );
  }

  const openView = (investor: Investor) => {
    // Reselecting the open investor must not reset its drawer or an upload in progress.
    if (selected?.id === investor.id) {
      return;
    }
    if (selected && slideOverDirty &&
        // eslint-disable-next-line no-alert
        !window.confirm('Discard unsaved changes and switch investors?')) {
      return;
    }
    setSelectedEditOnOpen(false);
    setSelected(investor);
  };
  const openCreate = (channel?: ChannelView) => {
    setCreateChannel(channel);
    setCreateOpen(true);
  };
  const openEdit = (investor: Investor, _channel?: ChannelView) => {
    if (selected && selected.id !== investor.id && slideOverDirty &&
        // eslint-disable-next-line no-alert
        !window.confirm('Discard unsaved changes and switch investors?')) {
      return;
    }
    setCreateOpen(false);
    setCreateChannel(undefined);
    setSelectedEditOnOpen(true);
    setSelected(investor);
  };

  const handleInvestorUpdated = (updated: Investor) => {
    setSlideOverDirty(false);
    setSelectedEditOnOpen(false);
    setSelected(updated);
    void refreshInvestor(updated.id);
  };

  const handleArchive = async (investorId: string) => {
    const investor = investors.find((item) => item.id === investorId);

    try {
      const archived = await archiveInvestor(investorId);
      if (selected?.id === investorId) {
        setSelected(null);
      }
      toast.success(`Archived "${archived.investor}"`);
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Could not archive "${investor?.investor || 'investor'}": ${reason}`);
      throw error;
    }
  };

  const handleBulkArchive = async (ids: string[]) => {
    try {
      const result = await bulkArchiveInvestors(ids);
      if (result.archived.length > 0) {
        toast.success(
          result.archived.length === 1
            ? `Archived "${result.archived[0].investor}"`
            : `Archived ${result.archived.length} investors`
        );
      }
      if (result.failed.length > 0) {
        const firstFailure = result.failed[0];
        toast.error(
          `${result.failed.length} investor${result.failed.length === 1 ? '' : 's'} could not be archived. ` +
          `${firstFailure.investorName}: ${firstFailure.error}`
        );
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The investors could not be archived');
      throw error;
    }
  };

  const handleOpenArchived = async () => {
    setArchivedOpen(true);
    try {
      await loadArchivedInvestors();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Archived investors could not be loaded');
    }
  };

  const handleRestore = async (investorId: string) => {
    const restored = await restoreInvestor(investorId);
    toast.success(`Restored "${restored.investor}"`);
  };

  const handleBulkImport = async (imported: Investor[]) => {
    // addInvestor toasts each row's specific failure; summarize accurate counts here.
    const results = await Promise.allSettled(imported.map((inv) => addInvestor(inv)));
    const failed = results.filter((result) => result.status === 'rejected').length;
    const succeeded = imported.length - failed;
    if (succeeded > 0) {
      toast.success(succeeded === 1 ? 'Imported 1 investor' : `Imported ${succeeded} investors`);
    }
    if (failed > 0) {
      toast.error(`${failed} of ${imported.length} imports failed`);
    }
  };

  // Roster inline edits → scoped patch via updateInvestorFields (only changed,
  // allow-listed fields), avoiding the whole-record lost-update risk.
  const handleDashboardInlineUpdate = async (updated: Investor) => {
    const prev = investors.find((i) => i.id === updated.id);
    if (!prev || !organizationId) {
      await updateInvestor(updated);
      return;
    }
    const patch: Partial<Investor> = {};
    for (const key of INLINE_DIFF_KEYS) {
      if (JSON.stringify(prev[key]) !== JSON.stringify(updated[key])) {
        (patch as Record<string, unknown>)[key] = updated[key];
      }
    }
    if (JSON.stringify(prev.netWorthRequirements) !== JSON.stringify(updated.netWorthRequirements)) {
      patch.netWorthRequirements = updated.netWorthRequirements;
    }
    if (Object.keys(patch).length === 0) {
      return;
    }
    try {
      await InvestorService.updateInvestorFields(updated.id, patch, organizationId);
      await refreshInvestor(updated.id);
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : 'Failed to save change');
      await refreshInvestor(updated.id);
    }
  };

  return (
    <InvestorThemeScope>
      {/* Roster stays mounted; detail/edit/add render as overlays. */}
      <DashboardV2
        investors={investors}
        onAddInvestor={openCreate}
        onViewInvestor={openView}
        onEditInvestor={openEdit}
        onArchiveInvestor={handleArchive}
        onBulkArchiveInvestors={handleBulkArchive}
        onOpenArchivedInvestors={canManage ? handleOpenArchived : undefined}
        onUpdateInvestor={handleDashboardInlineUpdate}
        onBulkImport={handleBulkImport}
        readOnly={!canManage}
        organizationId={organizationId}
        onRefreshInvestor={refreshInvestor}
        filters={filters}
        onFiltersChange={setFilters}
        sortField={sortField}
        onSortFieldChange={setSortField}
        sortDirection={sortDirection}
        onSortDirectionChange={setSortDirection}
        onOpenCommand={() => setPaletteOpen(true)}
      />

      <InvestorSlideOver
        investor={selected}
        open={Boolean(selected)}
        organizationId={organizationId ?? ''}
        canManage={canManage}
        onOpenChange={(o) => {
 if (!o) {
 setSelected(null); 
 setSelectedEditOnOpen(false);
 setSlideOverDirty(false);
} 
}}
        onInvestorUpdated={handleInvestorUpdated}
        editOnOpen={selectedEditOnOpen}
        onDirtyChange={setSlideOverDirty}
      />

      <InvestorCommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        investors={investors}
        onSelectInvestor={(inv) => {
 setPaletteOpen(false); openView(inv); 
}}
        onAddInvestor={canManage ? () => {
 setPaletteOpen(false); openCreate(undefined); 
} : undefined}
      />

      <ArchivedInvestorsDialog
        open={archivedOpen}
        onOpenChange={setArchivedOpen}
        investors={archivedInvestors}
        loading={archivedLoading}
        onRestore={handleRestore}
      />

      {canManage && (
        <AddInvestorWizard
          open={createOpen}
          defaultChannel={createChannel}
          existingInvestors={investors}
          onOpenChange={(open) => {
            setCreateOpen(open);
            if (!open) {
              setCreateChannel(undefined);
            }
          }}
          onCreate={addInvestor}
        />
      )}
    </InvestorThemeScope>
  );
};

export default InvestorsPageV2;
